import {
  SITE_NAME,
  breadcrumbJsonLd,
  buildPageHead,
  canonicalUrl,
  collectionPageJsonLd,
  jsonLdScript,
  getSiteOrigin,
  markdownAlternate,
  type HeadResult,
} from './siteMeta'

/**
 * Head builders for the primary static pages that are also prerendered into
 * their own dist HTML (so crawlers without JS get the full head). Shared by the
 * runtime routes and the build-time prerender script — single source of truth
 * for their title/description.
 */

export function buildSearchHead(origin: string = getSiteOrigin()): HeadResult {
  const description =
    'Procházejte aktuální last-minute nabídky ubytování v Česku. Filtrujte podle regionu, termínu, počtu osob i vybavení – chalupy, chaty, penziony i hotely.'
  const url = canonicalUrl('/ubytovani/hledat', origin)
  const base = buildPageHead({
    title: 'Vyhledávání ubytování – last-minute nabídky | Mátevolno',
    description,
    path: '/ubytovani/hledat',
    origin,
  })

  return {
    ...base,
    links: [...(base.links ?? []), markdownAlternate(`${url}.md`)],
    scripts: [
      jsonLdScript(
        breadcrumbJsonLd([
          { name: 'Domů', item: origin },
          { name: 'Vyhledávání ubytování', item: url },
        ]),
      ),
      jsonLdScript(
        collectionPageJsonLd('Vyhledávání last-minute ubytování – chalupy, chaty a penziony', description, url),
      ),
    ],
  }
}

export function buildInspirationListHead(origin: string = getSiteOrigin()): HeadResult {
  const description = 'Tipy, příběhy a inspirace na spontánní last-minute úniky po Česku.'
  const url = canonicalUrl('/inspirace', origin)
  const base = buildPageHead({
    title: `Inspirace | ${SITE_NAME}`,
    description,
    path: '/inspirace',
    origin,
  })

  return {
    ...base,
    links: [...(base.links ?? []), markdownAlternate(`${url}.md`)],
    scripts: [
      jsonLdScript(
        breadcrumbJsonLd([
          { name: 'Domů', item: origin },
          { name: 'Inspirace', item: url },
        ]),
      ),
      jsonLdScript(collectionPageJsonLd('Inspirace', description, url)),
    ],
  }
}

export function buildCatalogHead(origin: string = getSiteOrigin()): HeadResult {
  const base = buildPageHead({
    title: `Všechna ubytování v nabídce | ${SITE_NAME}`,
    description:
      'Kompletní přehled chalup, chat, glampingů a penzionů na Mátevolno podle regionů. Prohlédněte si detaily a sledujte, kdy se uvolní termín na poslední chvíli.',
    path: '/ubytovani',
    origin,
  })

  return {
    ...base,
    links: [...(base.links ?? []), markdownAlternate(`${canonicalUrl('/ubytovani', origin)}.md`)],
  }
}

export function buildContactHead(origin: string = getSiteOrigin()): HeadResult {
  return buildPageHead({
    title: `Kontakt | ${SITE_NAME}`,
    description:
      'Kontaktujte tým Mátevolno – telefon +420 605 131 713, e-mail info@matevolno.cz. Poradíme s rezervací i s nabídkou vašeho ubytování.',
    path: '/kontakt',
    origin,
  })
}

export function buildPartnerHead(origin: string = getSiteOrigin()): HeadResult {
  return buildPageHead({
    title: `Nabídněte své ubytování | ${SITE_NAME}`,
    description:
      'Staňte se partnerem Mátevolno a snadno prodejte právě uvolněné termíny. Oslovte hosty, kteří hledají ubytování na poslední chvíli.',
    path: '/partner',
    origin,
  })
}
